<template>
  <div class="my-4">
    <v-card color="primary" class="fipa--header" tile>
      <div class="ml-auto d-flex">
        <v-btn
          v-if="canAdd"
          variant="text"
          class="btn-with-icon mx-2"
          data-test="horseOpenVaccinationEdit"
          @click="openEditForm"
        >
          <template v-if="editForm">
            <div class="btn-icon-with-text">
              <v-icon size="small" class="text-white">
                $iconTimesCircle
              </v-icon>
            </div>
            <span class="text-white">{{
              $t("horses:vaccinations.LeaveEditMode.label", "Leave edit mode")
            }}</span>
          </template>
          <template v-else>
            <div class="bg-edit btn-icon-with-text">
              <v-icon size="small" class="text-white"> $edit </v-icon>
            </div>
            <span class="text-white">{{
              $t("common:button.edit", "Edit")
            }}</span>
          </template>
        </v-btn>
      </div>
    </v-card>
    <v-card v-if="canAdd" v-show="editForm" color="strong" class="pa-4">
      <v-row dense>
        <v-col cols="12" order="2" order-md="1" md="4">
          <b>
            {{
              $t(
                "horses:vaccinations.NumberofRecords.label",
                "Number of records"
              )
            }}
          </b>
          <br />
          {{ vaccinations.length }}
        </v-col>
        <v-col cols="12" order="3" order-md="2" md="4">
          <b>
            {{
              $t(
                "horses:vaccinations.FirstVaccinationDate.label",
                "First vaccination date"
              )
            }}
          </b>
          <br />
          <template v-if="vaccinations.length > 0">
            {{ recentVaccine }}
          </template>
        </v-col>
        <v-col
          cols="12"
          order="1"
          order-md="3"
          md="4"
          class="text-center text-md-right"
        >
          <v-btn
            variant="text"
            class="btn-with-icon"
            data-test="addVacination"
            @click="openVaccinationForm"
          >
            <div class="bg-edit btn-icon-with-text">
              <v-icon size="small" class="text-white"> $iconPlus </v-icon>
            </div>
            <span style="align-self: center">{{
              $t("horses:vaccinations.AddVaccination.label", "Add vaccination")
            }}</span>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
    <v-data-table
      v-model:expanded="expanded"
      :headers="headers"
      :hide-default-header="$vuetify.display.smAndDown"
      :loading="$apollo.queries.vaccinations.loading || updateSubmitted"
      :items-per-page="-1"
      :items="vaccinations"
      single-expand
      class="entsys--table"
      hide-default-footer
    >
      <template v-if="canAdd" #body.prepend>
        <tr v-show="showVaccinationForm">
          <td colspan="99" class="pa-0">
            <v-card color="strong" class="pa-4" tile>
              <v-row dense>
                <v-col>
                  <div style="font-size: 18px">
                    <b>{{
                      $t(
                        "horses:vaccinations.AddNewVaccination.label",
                        "Add new vaccination"
                      )
                    }}</b>
                  </div>
                </v-col>
                <v-col class="text-right" cols="2">
                  <v-btn-icon
                    variant="text"
                    @click="() => (showVaccinationForm = false)"
                  >
                    <v-icon color="secondary"> $iconTimesCircle </v-icon>
                  </v-btn-icon>
                </v-col>
              </v-row>
              <v-sheet v-if="serverError" color="alert" class="pa-3">
                <h4>
                  <b>{{ serverError.message }}</b>
                </h4>
                <ul>
                  <li
                    v-for="(message, index) in serverError.messages"
                    :key="index + 'new'"
                  >
                    {{ displayText(message) }}
                  </li>
                </ul>
              </v-sheet>
              <v-row :no-gutters="$vuetify.display.xs">
                <v-col cols="12" md="4">
                  <AppDatePicker
                    v-model="form.vaccinationDate"
                    :error-messages="fieldErrorMessages('vaccinationDate')"
                    data-test="horseVaccinationDate"
                    :disabled="formSubmited"
                    :label="
                      $t(
                        'horses:vaccinations.VaccinationDate.label',
                        'Vaccination date'
                      )
                    "
                    autofocus
                    input-class="fipa-input-white-background"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="form.comment"
                    :label="$t('horses:vaccinations.comment.label', 'Comment')"
                    variant="outlined"
                    :disabled="formSubmited"
                    class="fipa-input-white-background"
                    rows="1"
                    data-test="horseVaccinationComment"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="form.veterinarianName"
                    :label="
                      $t(
                        'horses:vaccinations.veterinarian.label',
                        'Veterinarian'
                      )
                    "
                    :error-messages="fieldErrorMessages('veterinarianName')"
                    variant="outlined"
                    :disabled="formSubmited"
                    class="fipa-input-white-background"
                    data-test="horseVaccinationVeterinarian"
                  />
                </v-col>
              </v-row>
              <v-row
                dense
                style="justify-content: flex-end; align-items: center"
              >
                <div class="px-4">
                  <v-checkbox
                    v-model="addNext"
                    :disabled="formSubmited"
                    :label="$t('horses:vaccinations.AddNext.label', 'Add next')"
                    color="primary"
                    hide-details
                  />
                </div>
                <v-btn
                  color="primary"
                  :disabled="formSubmited"
                  :loading="formSubmited"
                  data-test="submitVaccination"
                  @click="handleSubmit"
                >
                  {{ $t("horses:vaccinations.PublishButton.label", "Publish") }}
                </v-btn>
              </v-row>
            </v-card>
          </td>
        </tr>
      </template>
      <template #item="{ item, toggleExpand, internalItem, index }">
        <AppHorseVaccinationRow :item="item" :recent-valid-check="index == 0">
          <template #actions-desktop>
            <td class="text-right px-0">
              <div v-if="editForm">
                <v-btn-icon
                  v-if="
                    $allowedMutation(
                      item.allowedMutations,
                      'horseUpdateVaccination'
                    )
                  "
                  icon
                  size="small"
                  class="bg-edit-green"
                  data-test="horseEditVaccination"
                  @click="editVaccination(toggleExpand, item, internalItem)"
                >
                  <v-icon size="small" color="white"> $edit </v-icon>
                </v-btn-icon>
                <v-btn-icon
                  v-if="
                    $allowedMutation(
                      item.allowedMutations,
                      'horseDeleteVaccination'
                    )
                  "
                  icon
                  size="small"
                  class="bg-remove"
                  data-test="horseDeleteVaccination"
                  @click="deleteVaccination(item, $event)"
                >
                  <v-icon size="small" color="white"> $iconTrash </v-icon>
                  <v-progress-circular
                    v-show="false"
                    color="white"
                    size="12"
                    indeterminate
                  />
                </v-btn-icon>
              </div>
            </td>
          </template>
          <template #actions-mobile>
            <div v-if="editForm">
              <v-btn-icon
                v-if="
                  $allowedMutation(
                    item.allowedMutations,
                    'horseUpdateVaccination'
                  )
                "
                icon
                size="small"
                class="bg-edit-green"
                :aria-label="$t('common:editButton.alt')"
                data-test="horseEditVaccination"
                @click="editVaccination(toggleExpand, item, internalItem)"
              >
                <v-icon size="small" color="white"> $edit </v-icon>
              </v-btn-icon>
              <v-btn-icon
                v-if="
                  $allowedMutation(
                    item.allowedMutations,
                    'horseDeleteVaccination'
                  )
                "
                icon
                size="small"
                class="bg-remove"
                :aria-label="$t('common:deleteButton.alt')"
                data-test="horseDeleteVaccination"
                @click="deleteVaccination(item, $event)"
              >
                <v-icon size="small" color="white"> $iconTrash </v-icon>
                <v-progress-circular
                  v-show="false"
                  color="white"
                  size="21"
                  indeterminate
                />
              </v-btn-icon>
            </div>
          </template>
        </AppHorseVaccinationRow>
      </template>
      <template #expanded-row="{ item }">
        <tr
          v-if="
            $allowedMutation(item.allowedMutations, 'horseUpdateVaccination')
          "
        >
          <td colspan="99" class="pa-0">
            <v-card color="strong" class="pa-4" tile>
              <v-row dense>
                <v-col>
                  <h3>
                    {{
                      $t(
                        "horses:vaccinations.UpdateVaccination.label",
                        "Update vaccination"
                      )
                    }}
                  </h3>
                </v-col>
                <v-col class="text-right">
                  <v-btn-icon variant="text" @click="() => (expanded = [])">
                    <v-icon size="large" color="secondary">
                      $iconTimesCircle
                    </v-icon>
                  </v-btn-icon>
                </v-col>
              </v-row>
              <v-sheet v-if="serverError" color="alert" class="pa-3">
                <h4>
                  <b>{{ serverError.message }}</b>
                </h4>
                <ul>
                  <li
                    v-for="(message, index) in serverError.messages"
                    :key="index + 'update'"
                  >
                    {{ displayText(message) }}
                  </li>
                </ul>
              </v-sheet>
              <v-row>
                <v-col cols="12" md="4">
                  <AppDatePicker
                    v-model="form.vaccinationDate"
                    :error-messages="fieldErrorMessages('vaccinationDate')"
                    data-test="horseVaccinationDate"
                    :disabled="updateSubmitted"
                    :label="
                      $t(
                        'horses:vaccinations.VaccinationDate.label',
                        'Vaccination date'
                      )
                    "
                    autofocus
                    input-class="fipa-input-white-background"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="form.comment"
                    :label="$t('horses:vaccinations.comment.label', 'Comment')"
                    variant="outlined"
                    :disabled="updateSubmitted"
                    class="fipa-input-white-background"
                    data-test="horseVaccinationComment"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="form.veterinarianName"
                    :label="
                      $t(
                        'horses:vaccinations.veterinarian.label',
                        'Veterinarian'
                      )
                    "
                    :error-messages="fieldErrorMessages('veterinarianName')"
                    variant="outlined"
                    :disabled="updateSubmitted"
                    class="fipa-input-white-background"
                    data-test="horseVaccinationVeterinarian"
                  />
                </v-col>
              </v-row>
              <v-row
                dense
                style="justify-content: flex-end; align-items: center"
              >
                <v-btn
                  color="primary"
                  :disabled="updateSubmitted"
                  :loading="updateSubmitted"
                  data-test="updateVaccination"
                  @click="updateVaccination"
                >
                  {{ $t("horses:vaccinations.UpdateButton.label", "Update") }}
                </v-btn>
              </v-row>
            </v-card>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>
<script>
import { ref } from "vue";
import gql from "fraql";
import { useSaveMeta } from "@/composables/form/save_meta";
import { useFormValidation } from "@/composables/form/form_validation";
import { orderBy } from "lodash-es";
import { useDisplayText } from "@composables/display_text";

import { refetchHorseVaccinationStatus } from "@AppShows/horse/AppShowHorseVaccinationStatus.vue";

import AppDatePicker from "@elements/AppDatePicker.vue";
import AppHorseVaccinationRow from "@elements/rows/AppHorseVaccinationRow.vue";
import { showLoadingButton } from "@/utils/showLoadingButton";

export default {
  name: "HorseVaccinationsSection",
  components: { AppDatePicker, AppHorseVaccinationRow },
  props: {
    horseId: { type: String, required: true },
    canAdd: { type: Boolean, default: false }
  },
  setup(props) {
    const form = ref({
      id: null,
      horseId: props.horseId,
      vaccinationDate: null,
      veterinarianName: "",
      comment: ""
    });

    const initVaccinationForm = ref({
      vuelidateValidations: {
        vaccinationDate: { required: true },
        veterinarianName: { required: true }
      },
      readOnlyFields: []
    });

    const { saveMetaSetOriginalForm, saveMetaConfirmUnsaveChanges } =
      useSaveMeta(form);

    const { v$, fieldDisabled, fieldErrorMessages } = useFormValidation(
      initVaccinationForm,
      form
    );

    const { displayText } = useDisplayText();
    return {
      initVaccinationForm,
      form,

      v$,
      fieldDisabled,
      fieldErrorMessages,

      displayText,
      saveMetaSetOriginalForm,
      saveMetaConfirmUnsaveChanges
    };
  },
  data() {
    return {
      vaccinations: [],
      expanded: [],
      showVaccinationForm: false,
      editForm: false,
      addNext: false,
      formSubmited: false,
      serverError: null,
      updateSubmitted: false
    };
  },
  computed: {
    recentVaccine() {
      return this.$dayjs.date(this.vaccinations[0].vaccinationDate);
    },
    headers() {
      return [
        {
          value: "vaccinationDate",
          title: this.$t("horses:vaccinations.date.label", "Date"),
          sortable: false
        },
        { value: "x", title: "", sortable: false },
        {
          value: "vacc_type",
          title: this.$t("horses:vaccinations.national_rules.label"),
          sortable: false
        },
        { value: "u", title: "", sortable: false },
        {
          value: "intl_vacc_type",
          title: this.$t("horses:vaccinations.international_rules.label"),
          sortable: false
        },
        {
          value: "comment",
          title: this.$t("horses:vaccinations.comment.label", "Comment"),
          sortable: false
        },
        {
          value: "veterinarian",
          title: this.$t(
            "horses:vaccinations.veterinarian.label",
            "Veterinarian"
          ),
          sortable: false
        },
        {
          value: "source",
          title: this.$t("horses:vaccinations.source.label", "Source"),
          sortable: false
        },
        { value: "", title: "", sortable: false }
      ];
    },
    inititalForm() {
      return {
        id: null,
        horseId: this.horseId,
        vaccinationDate: null,
        veterinarianName: "",
        comment: ""
      };
    }
  },
  watch: {
    async showVaccinationForm(newVal) {
      if (newVal === false) {
        if ((await this.saveMetaConfirmUnsaveChanges()) === true) {
          this.showVaccinationForm = false;
          this.form = JSON.parse(JSON.stringify(this.inititalForm));
          this.saveMetaSetOriginalForm(this.form);
        } else {
          this.showVaccinationForm = true;
        }
      }
    }
  },
  apollo: {
    vaccinations: {
      query: gql`
        query horse($horseId: ID!) {
          horse(id: $horseId) {
            id
            vaccinations {
              ${AppHorseVaccinationRow.fragments.vaccination}
              allowedMutations
            }
          }
        }
      `,
      variables() {
        return { horseId: this.horseId };
      },
      update({ horse }) {
        return orderBy(horse.vaccinations, "vaccinationDate", "desc");
      }
    }
  },
  methods: {
    openEditForm() {
      if (this.editForm) {
        this.showVaccinationForm = false;
        this.addNext = false;
        this.editForm = false;
      } else {
        this.editForm = true;
      }
    },
    openVaccinationForm() {
      this.v$.form.$reset();
      this.form = JSON.parse(JSON.stringify(this.inititalForm));
      this.saveMetaSetOriginalForm(this.form);
      this.showVaccinationForm = this.addNext || !this.showVaccinationForm;
    },
    editVaccination(toggleExpand, item, internalItem) {
      this.v$.form.$reset();
      this.form = {
        id: item.id,
        vaccinationDate: item.vaccinationDate,
        veterinarianName: item.veterinarianName,
        comment: item.comment
      };

      this.saveMetaSetOriginalForm(this.form);
      toggleExpand(internalItem);
    },
    handleSubmit() {
      this.serverError = null;
      this.v$.form.$touch();
      if (this.v$.form.$invalid) {
        return false;
      }
      this.v$.form.$reset();
      this.formSubmited = true;
      // Mutation
      this.$apollo
        .mutate({
          mutation: gql`
            mutation horseAddVaccination(
              $horseId: ID!
              $comment: String
              $vaccinationDate: String!
              $veterinarianName: String!
            ) {
              horseAddVaccination(
                input: {
                  horseId: $horseId
                  comment: $comment
                  vaccinationDate: $vaccinationDate
                  veterinarianName: $veterinarianName
                }
              ) {
                vaccination {
                  horseId
                  comment
                  veterinarianName
                  courseStage
                }
              }
            }
          `,
          variables: { ...this.form, ...{ horseId: this.horseId } },
          refetchQueries: ["horse"]
        })
        .then(() => {
          refetchHorseVaccinationStatus(this.horseId);

          // Result
          this.form = JSON.parse(JSON.stringify(this.inititalForm));
          this.saveMetaSetOriginalForm(this.form);
          this.showVaccinationForm = this.addNext || !this.showVaccinationForm;
          this.$root.$snackbar("success");
          this.formSubmited = false;
        })
        .catch(error => {
          // Error
          this.serverError = error.graphQLErrors[0];
          this.formSubmited = false;
        });
    },
    updateVaccination() {
      this.serverError = null;
      this.v$.form.$touch();
      if (this.v$.form.$invalid) {
        return false;
      }
      this.v$.form.$reset();
      this.updateSubmitted = true;
      // Mutation
      this.$apollo
        .mutate({
          mutation: gql`
            mutation horseUpdateVaccination(
              $id: ID!
              $comment: String
              $vaccinationDate: String!
              $veterinarianName: String!
            ) {
              horseUpdateVaccination(
                input: {
                  id: $id
                  comment: $comment
                  vaccinationDate: $vaccinationDate
                  veterinarianName: $veterinarianName
                }
              ) {
                vaccination {
                  horseId
                  comment
                  veterinarianName
                  courseStage
                }
              }
            }
          `,
          variables: this.form,
          refetchQueries: ["horse"]
        })
        .then(() => {
          refetchHorseVaccinationStatus(this.horseId);
          // Result
          this.saveMetaSetOriginalForm(this.form);
          this.expanded = [];
          this.form = JSON.parse(JSON.stringify(this.inititalForm));
          this.$root.$snackbar("success", { text: "Updated!" });
          this.updateSubmitted = false;
        })
        .catch(error => {
          // Error
          this.serverError = error.graphQLErrors[0];
          this.updateSubmitted = false;
        });
    },
    deleteVaccination(item, node) {
      this.$root
        .$confirm(
          this.$t("horses:vaccinations.deleteConfirm.heading"),
          this.$t("horses:vaccinations.deleteConfirm.question")
        )
        .then(async confirm => {
          if (confirm) {
            const removeLoading = showLoadingButton(node.target);
            this.$apollo
              .mutate({
                mutation: gql`
                  mutation horseDeleteVaccination($id: ID!, $horseId: ID!) {
                    horseDeleteVaccination(
                      input: { id: $id, horseId: $horseId }
                    ) {
                      horse {
                        id
                      }
                    }
                  }
                `,
                variables: {
                  id: item.id,
                  horseId: this.horseId
                },
                refetchQueries: ["horse"]
              })
              .then(() => {
                refetchHorseVaccinationStatus(this.horseId);

                // Result)
                this.expanded = [];
                removeLoading();
                this.$root.$snackbar("success", {
                  text: this.$t(
                    "common:feedbackMessage.removedSuccessfully.text",
                    "Succesfully deleted!"
                  )
                });
              })
              .catch(error => {
                // Error
                removeLoading();
                this.$root.$snackbar("error", { text: error });
                throw error;
              });
          }
        });
    }
  }
};
</script>
